/**
 * The tag input in the page header (`070-search-and-links.md` §6).
 *
 * "Free-form, created on the fly. There is no 'manage tags' step before you can use one —
 * **that friction is why tag systems die.**"
 *
 * The one rule that is easy to get wrong: **case-insensitive reuse.** Typing `NPC` when
 * `npc` exists must REUSE `npc`. It is not a second tag and it is not an error. So the
 * client resolves the name against the cached list before it ever offers "Create tag" —
 * which means the server's `409` on `UNIQUE (owner_id, lower(name))` is reached only by a
 * genuine two-tab race, and `OutboxBridge` handles that by re-fetching the list.
 *
 * Assignment is ALWAYS `PATCH /api/pages/{id}` with the full `tagIds` set. There is no
 * add-one/remove-one endpoint: one writer, one shape, no drift.
 */
import { useMemo, useRef, useState } from 'react';
import { Plus, Tag as TagIcon, X } from 'lucide-react';
import type { PageTag } from '@/api/endpoints/pages';
import { findTagByName, isValidTagName, type Tag } from '@/api/endpoints/tags';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from '@/components/ui/sonner';
import { useTranslation } from '@/lib/i18n';
import { newTagId, useCreateTag, useSetPageTags, useTags } from '@/features/tags/useTags';

export function TagChip({ tag, onRemove }: { tag: PageTag; onRemove?: () => void }) {
  const { t } = useTranslation();

  return (
    <span
      data-testid={`tag-chip-${tag.name}`}
      className="group inline-flex h-5 items-center gap-1 rounded-sm border border-[var(--border)] bg-[var(--muted)] px-1.5 text-2xs"
      style={
        // Colour is a convenience, never a semantic — the LABEL is always shown (`070` §6).
        tag.color === null ? undefined : { borderColor: tag.color, color: tag.color }
      }
    >
      {tag.name}
      {onRemove !== undefined && (
        <button
          type="button"
          aria-label={t('tags.removeTag', { name: tag.name })}
          onClick={onRemove}
          className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
        >
          <X className="size-3" strokeWidth={2} />
        </button>
      )}
    </span>
  );
}

export function TagInput({ pageId, tags }: { pageId: string; tags: PageTag[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const all = useTags();
  const { mutate: createTag } = useCreateTag();
  const { mutate: setPageTags } = useSetPageTags();

  const assigned = useMemo(() => new Set(tags.map((tag) => tag.id)), [tags]);

  const candidates = useMemo(() => {
    const list = all.data?.tags ?? [];
    const needle = query.trim().toLowerCase();

    return list.filter(
      (tag) => !assigned.has(tag.id) && (needle === '' || tag.name.toLowerCase().includes(needle)),
    );
  }, [all.data, assigned, query]);

  /** Is the typed name an EXACT (case-insensitive) match for a tag that already exists? */
  const existing: Tag | undefined = useMemo(
    () => findTagByName(all.data?.tags ?? [], query),
    [all.data, query],
  );

  const replace = (tagIds: string[]): void => {
    setPageTags({ pageId, tagIds });
  };

  const assign = (tagId: string): void => {
    if (assigned.has(tagId)) return;
    replace([...tags.map((tag) => tag.id), tagId]);
    setQuery('');
    inputRef.current?.focus();
  };

  const remove = (tagId: string): void => {
    replace(tags.filter((tag) => tag.id !== tagId).map((tag) => tag.id));
  };

  /**
   * Enter on "Create tag 'foo'". Two writes, both through the outbox, in FIFO order:
   * `tag.create` (with the id we mint here) and then `page.update` with the full set that
   * contains it. The queue guarantees the tag exists before the page references it.
   */
  const create = (): void => {
    const name = query.trim();
    if (!isValidTagName(name)) {
      toast.error(t('tags.toast.invalidName'));
      return;
    }

    // Case-insensitive REUSE, before the server ever hears about it (`070` §6).
    const match = findTagByName(all.data?.tags ?? [], name);
    if (match !== undefined) {
      assign(match.id);
      return;
    }

    const id = newTagId();
    createTag({ id, name });
    assign(id);
  };

  const canCreate = query.trim() !== '' && existing === undefined;

  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="page-tags">
      {tags.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          onRemove={() => {
            remove(tag.id);
          }}
        />
      ))}

      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery('');
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t('tags.addTag')}
            data-testid="add-tag"
            className="inline-flex h-5 items-center gap-0.5 rounded-sm px-1 text-2xs text-[var(--muted-foreground)] hover:bg-[var(--accent)] hover:text-[var(--foreground)]"
          >
            <TagIcon className="size-3" strokeWidth={1.75} />
            {tags.length === 0 && <span>{t('tags.addTag')}</span>}
            <Plus className="size-3" strokeWidth={2} />
          </button>
        </PopoverTrigger>

        <PopoverContent align="start" className="w-64 p-0">
          <Command
            // We filter above (and the "Create tag" row must survive whatever is typed);
            // cmdk's own fuzzy filter would fight both.
            shouldFilter={false}
          >
            <CommandInput
              ref={inputRef}
              value={query}
              onValueChange={setQuery}
              placeholder={t('tags.input.placeholder')}
              data-testid="tag-input"
            />
            <CommandList>
              {candidates.length === 0 && !canCreate && (
                <CommandEmpty>
                  {query === '' ? t('tags.empty.none') : t('tags.empty.alreadyOn')}
                </CommandEmpty>
              )}

              <CommandGroup>
                {candidates.map((tag) => (
                  <CommandItem
                    key={tag.id}
                    value={tag.id}
                    onSelect={() => {
                      assign(tag.id);
                    }}
                  >
                    <TagIcon
                      strokeWidth={1.75}
                      style={tag.color === null ? undefined : { color: tag.color }}
                    />
                    <span className="truncate">{tag.name}</span>
                  </CommandItem>
                ))}

                {/* Always the LAST row, and only when nothing matches exactly (`070` §6). */}
                {canCreate && (
                  <CommandItem value="__create__" onSelect={create} data-testid="create-tag">
                    <Plus strokeWidth={2} />
                    <span className="truncate">
                      {t('tags.createTagBefore')}
                      <span className="font-medium">{query.trim()}</span>
                      {t('tags.createTagAfter')}
                    </span>
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
